// components/admin/EventForm.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Button from '../common/Button';
import Input from '../common/Input';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase';

const CATEGORIES = [
  { id: 'karaoke', label: 'Karaoke' },
  { id: 'parti', label: 'Parti' },
  { id: 'söyleşi', label: 'Söyleşi' },
  { id: 'konser', label: 'Konser' },
  { id: 'atölye', label: 'Atölye' },
  { id: 'workshop', label: 'Workshop' },
];

const EventForm = ({ event, onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    location: '',
    category: 'konser',
    price: '',
    image: '',
    featured: false,
  });

  const [errors, setErrors] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    if (event) {
      const eventDate = event.date instanceof Date 
        ? event.date 
        : new Date(event.date);
      
      setFormData({
        title: event.title || '',
        description: event.description || '',
        date: eventDate.toISOString().split('T')[0],
        time: event.time || '',
        location: event.location || '',
        category: event.category || 'konser',
        price: event.price || '',
        image: event.image || '',
        featured: event.featured || false,
      });
      
      // Mevcut görsel varsa önizlemeyi ayarla
      if (event.image) {
        setImagePreview(event.image);
      }
    }
  }, [event]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Dosya tipi kontrolü
      if (!file.type.startsWith('image/')) {
        alert('Lütfen bir görsel dosyası seçin');
        return;
      }
      
      // Dosya boyutu kontrolü (5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Görsel dosyası 5MB\'dan küçük olmalıdır');
        return;
      }
      
      setImageFile(file);
      
      // Önizleme oluştur
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview('');
    setFormData((prev) => ({ ...prev, image: '' }));
  };

  const uploadImage = async () => {
    if (!imageFile) return formData.image; // Mevcut URL'i kullan

    try {
      setUploadingImage(true);
      const timestamp = Date.now();
      const fileName = `events/${timestamp}_${imageFile.name}`;
      const storageRef = ref(storage, fileName);
      
      await uploadBytes(storageRef, imageFile);
      const downloadURL = await getDownloadURL(storageRef);
      
      return downloadURL;
    } catch (error) {
      console.error('Görsel yükleme hatası:', error);
      throw new Error('Görsel yüklenemedi');
    } finally {
      setUploadingImage(false);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Başlık gerekli';
    if (!formData.description.trim()) newErrors.description = 'Açıklama gerekli';
    if (!formData.date) newErrors.date = 'Tarih gerekli';
    if (!formData.time) newErrors.time = 'Saat gerekli';
    if (!formData.location.trim()) newErrors.location = 'Konum gerekli';
    if (!formData.category) newErrors.category = 'Kategori gerekli';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      try {
        // Önce görseli yükle
        const imageUrl = await uploadImage();
        
        const submitData = {
          ...formData,
          date: new Date(formData.date),
          image: imageUrl,
        };
        onSubmit(submitData);
      } catch (error) {
        alert('Hata: ' + error.message);
      }
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Form - Sol Taraf */}
      <div>
        <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Başlık */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-200 mb-2">
            Etkinlik Başlığı *
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            placeholder="Örn: Akustik Gece"
            className={`w-full px-4 py-3 rounded-lg border bg-gray-800 text-white placeholder-gray-400 ${
              errors.title ? 'border-red-500' : 'border-gray-600'
            } focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all`}
          />
          {errors.title && (
            <p className="mt-1 text-sm text-red-500">{errors.title}</p>
          )}
        </div>

        {/* Açıklama */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-200 mb-2">
            Açıklama *
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={4}
            placeholder="Etkinlik hakkında detaylı bilgi..."
            className={`w-full px-4 py-3 rounded-lg border bg-gray-800 text-white placeholder-gray-400 ${
              errors.description ? 'border-red-500' : 'border-gray-600'
            } focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all resize-none`}
          />
          {errors.description && (
            <p className="mt-1 text-sm text-red-500">{errors.description}</p>
          )}
        </div>

        {/* Tarih */}
        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            Tarih *
          </label>
          <input
            type="date"
            name="date"
            value={formData.date}
            onChange={handleChange}
            className={`w-full px-4 py-3 rounded-lg border bg-gray-800 text-white ${
              errors.date ? 'border-red-500' : 'border-gray-600'
            } focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all`}
          />
          {errors.date && (
            <p className="mt-1 text-sm text-red-500">{errors.date}</p>
          )}
        </div>

        {/* Saat */}
        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            Saat *
          </label>
          <input
            type="time"
            name="time"
            value={formData.time}
            onChange={handleChange}
            className={`w-full px-4 py-3 rounded-lg border bg-gray-800 text-white ${
              errors.time ? 'border-red-500' : 'border-gray-600'
            } focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all`}
          />
          {errors.time && (
            <p className="mt-1 text-sm text-red-500">{errors.time}</p>
          )}
        </div>

        {/* Konum */}
        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            Konum *
          </label>
          <input
            type="text"
            name="location"
            value={formData.location}
            onChange={handleChange}
            placeholder="Örn: Harmony Lab Studio"
            className={`w-full px-4 py-3 rounded-lg border bg-gray-800 text-white placeholder-gray-400 ${
              errors.location ? 'border-red-500' : 'border-gray-600'
            } focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all`}
          />
          {errors.location && (
            <p className="mt-1 text-sm text-red-500">{errors.location}</p>
          )}
        </div>

        {/* Kategori */}
        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            Kategori *
          </label>
          <select
            name="category"
            value={formData.category}
            onChange={handleChange}
            className={`w-full px-4 py-3 rounded-lg border bg-gray-800 text-white ${
              errors.category ? 'border-red-500' : 'border-gray-600'
            } focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all`}
          >
            {CATEGORIES.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.label}
              </option>
            ))}
          </select>
          {errors.category && (
            <p className="mt-1 text-sm text-red-500">{errors.category}</p>
          )}
        </div>

        {/* Fiyat */}
        <div>
          <label className="block text-sm font-medium text-gray-200 mb-2">
            Fiyat
          </label>
          <input
            type="text"
            name="price"
            value={formData.price}
            onChange={handleChange}
            placeholder="Örn: 50₺ veya Ücretsiz"
            className="w-full px-4 py-3 rounded-lg border bg-gray-800 text-white placeholder-gray-400 border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
          />
        </div>

        {/* Görsel Yükleme */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-200 mb-2">
            Etkinlik Görseli
          </label>
          
          {imagePreview ? (
            <div className="space-y-3">
              <div className="relative inline-block">
                <img
                  src={imagePreview}
                  alt="Önizleme"
                  className="w-full max-w-md h-48 object-cover rounded-lg border-2 border-gray-600"
                />
                <button
                  type="button"
                  onClick={removeImage}
                  className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full hover:bg-red-700 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-sm text-gray-400">Görseli değiştirmek için aşağıdan yeni bir dosya seçin</p>
            </div>
          ) : (
            <div className="flex items-center justify-center w-full">
              <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-gray-600 border-dashed rounded-lg cursor-pointer bg-gray-800 hover:bg-gray-700 transition-colors">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <svg className="w-12 h-12 mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="mb-2 text-sm text-gray-300">
                    <span className="font-semibold">Tıklayın</span> veya sürükleyin
                  </p>
                  <p className="text-xs text-gray-500">PNG, JPG, JPEG (MAX. 5MB)</p>
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </label>
            </div>
          )}
        </div>

        {/* Öne Çıkan */}
        <div className="md:col-span-2">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              name="featured"
              checked={formData.featured}
              onChange={handleChange}
              className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <span className="text-sm font-medium text-gray-200">
              Öne çıkan etkinlik olarak göster
            </span>
          </label>
          <p className="mt-1 text-xs text-gray-500">
            Öne çıkan etkinlikler ana sayfada ve etkinlikler sayfasının üst kısmında gösterilir.
          </p>
        </div>
      </div>

      {/* Butonlar */}
      <div className="flex justify-end space-x-4 pt-6 border-t border-gray-700">
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-3 text-gray-200 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors font-medium"
        >
          İptal
        </button>
        <Button
          type="submit"
          disabled={loading || uploadingImage}
          className="px-6 py-3"
        >
          {loading || uploadingImage ? (
            <span className="flex items-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {uploadingImage ? 'Görsel Yükleniyor...' : 'Kaydediliyor...'}
            </span>
          ) : event ? 'Güncelle' : 'Etkinlik Oluştur'}
        </Button>
      </div>
    </form>
      </div>

      {/* Önizleme - Sağ Taraf */}
      <div className="sticky top-6 h-fit">
        <div className="bg-gray-900 rounded-lg p-4 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
            <svg className="w-5 h-5 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            Canlı Önizleme
          </h3>
          
          {/* Etkinlik Kartı Önizlemesi */}
          <div className="bg-gray-800 rounded-lg overflow-hidden shadow-lg border border-gray-700 hover:shadow-xl transition-shadow">
            {/* Görsel */}
            <div className="relative h-48 bg-gradient-to-br from-indigo-500 to-purple-600">
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt={formData.title || 'Etkinlik'}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <svg className="w-16 h-16 text-white/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              )}
              
              {/* Kategori Badge */}
              {formData.category && (
                <div className="absolute top-3 left-3">
                  <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                    formData.category === 'karaoke' ? 'bg-pink-600 text-white' :
                    formData.category === 'parti' ? 'bg-purple-600 text-white' :
                    formData.category === 'söyleşi' ? 'bg-blue-600 text-white' :
                    formData.category === 'konser' ? 'bg-green-600 text-white' :
                    formData.category === 'atölye' ? 'bg-yellow-600 text-white' :
                    formData.category === 'workshop' ? 'bg-orange-600 text-white' :
                    'bg-gray-600 text-white'
                  }`}>
                    {CATEGORIES.find(c => c.id === formData.category)?.label || formData.category}
                  </span>
                </div>
              )}
              
              {/* Öne Çıkan Badge */}
              {formData.featured && (
                <div className="absolute top-3 right-3">
                  <span className="inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full bg-indigo-600 text-white">
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    Öne Çıkan
                  </span>
                </div>
              )}
            </div>
            
            {/* İçerik */}
            <div className="p-4 space-y-3">
              {/* Başlık */}
              <h4 className="text-lg font-bold text-white line-clamp-2">
                {formData.title || 'Etkinlik Başlığı'}
              </h4>
              
              {/* Tarih & Saat */}
              <div className="flex items-center text-sm text-gray-300">
                <svg className="w-4 h-4 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>
                  {formData.date ? new Date(formData.date).toLocaleDateString('tr-TR', { 
                    day: 'numeric', 
                    month: 'long', 
                    year: 'numeric' 
                  }) : 'Tarih seçilmedi'}
                </span>
                {formData.time && (
                  <span className="ml-2">• {formData.time}</span>
                )}
              </div>
              
              {/* Konum */}
              {formData.location && (
                <div className="flex items-center text-sm text-gray-300">
                  <svg className="w-4 h-4 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>{formData.location}</span>
                </div>
              )}
              
              {/* Açıklama */}
              <p className="text-sm text-gray-400 line-clamp-3">
                {formData.description || 'Etkinlik açıklaması buraya gelecek...'}
              </p>
              
              {/* Fiyat */}
              <div className="pt-2 border-t border-gray-700">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Fiyat:</span>
                  <span className="text-lg font-bold text-blue-400">
                    {formData.price || 'Ücretsiz'}
                  </span>
                </div>
              </div>
              
              {/* Detay Butonu */}
              {event?.id ? (
                <Link 
                  to={`/events/${event.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full mt-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-center text-sm font-medium transition-colors"
                >
                  Detayları Gör
                </Link>
              ) : (
                <div className="w-full mt-2 px-4 py-2 bg-gray-600 text-gray-400 rounded-lg text-center text-sm font-medium cursor-not-allowed">
                  Detayları Gör (Önce kaydedin)
                </div>
              )}
            </div>
          </div>
          
          <p className="text-xs text-gray-500 mt-3 text-center">
            * Bu önizleme, etkinliğin sitede nasıl görüneceğini gösterir
          </p>
        </div>
      </div>
    </div>
  );
};

export default EventForm;
